import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Stack,
  Grid,
  CircularProgress,
  Typography,
  Chip,
} from "@mui/material";
import useApi from "../../hooks/useApi";
import useLocalization from "../../hooks/useLocalization";
import QADetail from "./QADetail";

const QA_DEFAULT_PAGE_SIZE = 50;
const QA_PAGE_SIZE_OPTIONS = [25, 50, 75, 100];

const QA = () => {
  const { postData } = useApi();
  const { provinceList } = useLocalization();

  const [answers, setAnswers] = useState([]);
  const [selectedAnswerIndex, setSelectedAnswerIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);
  const [pageSize, setPageSize] = useState(QA_DEFAULT_PAGE_SIZE);

  // Filters
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [clientId, setClientId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [repId, setRepId] = useState("");
  const [province, setProvince] = useState("");
  const [surveyQuestionId, setSurveyQuestionId] = useState("");
  const [reviewRequestedOnly, setReviewRequestedOnly] = useState("all");

  // Clients, Projects, and Reps for dropdowns
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [reps, setReps] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [repsLoading, setRepsLoading] = useState(true);
  const [questionsLoading, setQuestionsLoading] = useState(true);

  // Active row ref for keyboard shortcuts
  const keyboardRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const rowRefs = useRef({});

  const fetchQAData = useCallback(
    async (pageNumber, append = false, pageSizeOverride = null) => {
      try {
        if (append) {
          setLoadingMore(true);
        } else {
          setLoading(true);
        }
        setError(null);

        const effectivePageSize = pageSizeOverride || pageSize;

        const filter = {};
        if (dateStart) filter.dateRangeStart = dateStart;
        if (dateEnd) filter.dateRangeEnd = dateEnd;
        if (projectId) filter.projectId = projectId;
        if (repId) filter.repId = repId;
        if (province) filter.province = province;
        if (surveyQuestionId) filter.surveyQuestionId = surveyQuestionId;
        if (reviewRequestedOnly === "review") {
          filter.reviewRequestedOnly = true;
        }

        const response = await postData({
          entityName: "SurveyAnswer",
          action: "getQAData",
          data: {
            filter,
            pageNumber,
            recordsPerPage: effectivePageSize,
          },
        });

        if (response?.isSuccessful) {
          const rows = Array.isArray(response.data) ? response.data : [];

          setAnswers((prev) => (append ? [...prev, ...rows] : rows));
          setCurrentPage(pageNumber);
          setHasMore(rows.length === effectivePageSize);

          if (!append) {
            rowRefs.current = {};
            scrollContainerRef.current?.scrollTo({ top: 0, behavior: "auto" });
          }

          if (!append) {
            setSelectedAnswerIndex(0);
          }
        }
      } catch (err) {
        setError(err.message);
        console.error("Failed to fetch QA data:", err);
      } finally {
        if (append) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [
      dateStart,
      dateEnd,
      projectId,
      repId,
      province,
      surveyQuestionId,
      reviewRequestedOnly,
      pageSize,
      postData,
    ],
  );

  const handleSearch = useCallback(() => {
    fetchQAData(1, false);
  }, [fetchQAData]);

  const handleLoadMore = useCallback(() => {
    if (loadingMore || loading || !hasMore) return;
    fetchQAData(currentPage + 1, true);
  }, [fetchQAData, loadingMore, loading, hasMore, currentPage]);

  const handlePageSizeChange = useCallback(
    (event) => {
      const nextPageSize = Number(event.target.value);
      setPageSize(nextPageSize);
      fetchQAData(1, false, nextPageSize);
    },
    [fetchQAData],
  );

  const provinces = provinceList();

  const sortByName = useCallback((list) => {
    const items = Array.isArray(list) ? [...list] : [];
    items.sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), undefined, {
        sensitivity: "base",
      }),
    );
    return items;
  }, []);

  const sortedClients = useMemo(
    () => sortByName(clients),
    [clients, sortByName],
  );
  const sortedProjects = useMemo(
    () => sortByName(projects),
    [projects, sortByName],
  );
  const sortedReps = useMemo(() => sortByName(reps), [reps, sortByName]);
  const sortedQuestions = useMemo(
    () => sortByName(questions),
    [questions, sortByName],
  );
  const sortedProvinces = useMemo(
    () => sortByName(provinces),
    [provinces, sortByName],
  );

  // Fetch clients once for project narrowing filter
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await postData({
          entityName: "Client",
          action: "get",
          filter: { isActive: true },
          data: {},
        });

        if (response?.isSuccessful && Array.isArray(response.data)) {
          setClients(response.data);
        } else {
          setClients([]);
        }
      } catch (err) {
        console.error("Failed to fetch clients:", err);
        setClients([]);
      } finally {
        setClientsLoading(false);
      }
    };

    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch active projects; optionally narrow by selected client
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setProjectsLoading(true);
        const response = await postData({
          entityName: "Project",
          action: "get",
          filter: {
            isActive: true,
            ...(clientId ? { clientId } : {}),
          },
          data: {},
        });

        if (response?.isSuccessful && Array.isArray(response.data)) {
          setProjects(response.data);
        } else {
          setProjects([]);
        }
      } catch (err) {
        console.error("Failed to fetch projects:", err);
        setProjects([]);
      } finally {
        setProjectsLoading(false);
      }
    };

    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    setProjectId("");
    setSurveyQuestionId("");
  }, [clientId]);

  // Fetch managed reps with QA counts
  useEffect(() => {
    const fetchReps = async () => {
      try {
        const response = await postData({
          entityName: "SurveyAnswer",
          action: "getQAReps",
          data: {},
        });

        if (response?.isSuccessful && response.data) {
          setReps(response.data);
        }
      } catch (err) {
        console.error("Failed to fetch reps:", err);
      } finally {
        setRepsLoading(false);
      }
    };

    fetchReps();
  }, []); // Only run once on mount

  // Fetch QA questions for question filter (scoped by current non-question filters)
  useEffect(() => {
    let isMounted = true;

    const fetchQuestions = async () => {
      try {
        setQuestionsLoading(true);

        const filter = {};
        if (dateStart) filter.dateRangeStart = dateStart;
        if (dateEnd) filter.dateRangeEnd = dateEnd;
        if (projectId) filter.projectId = projectId;
        if (repId) filter.repId = repId;
        if (province) filter.province = province;
        if (reviewRequestedOnly === "review") {
          filter.reviewRequestedOnly = true;
        }

        const response = await postData({
          entityName: "SurveyAnswer",
          action: "getQAQuestions",
          data: { filter },
        });

        if (!isMounted) return;

        if (response?.isSuccessful && Array.isArray(response.data)) {
          setQuestions(response.data);

          if (
            surveyQuestionId &&
            !response.data.some((q) => q?.id === surveyQuestionId)
          ) {
            setSurveyQuestionId("");
          }
        } else {
          setQuestions([]);
        }
      } catch (err) {
        if (!isMounted) return;
        setQuestions([]);
        console.error("Failed to fetch QA questions:", err);
      } finally {
        if (isMounted) {
          setQuestionsLoading(false);
        }
      }
    };

    fetchQuestions();

    return () => {
      isMounted = false;
    };
  }, [
    dateStart,
    dateEnd,
    projectId,
    repId,
    province,
    reviewRequestedOnly,
    surveyQuestionId,
  ]);

  // Keyboard event handler for active row
  useEffect(() => {
    const isTypingTarget = (target) => {
      if (!target || !(target instanceof Element)) return false;

      const tagName = target.tagName?.toLowerCase?.();
      if (["input", "textarea", "select"].includes(tagName)) return true;
      if (target.isContentEditable) return true;

      return Boolean(
        target.closest("input, textarea, select, [contenteditable='true']"),
      );
    };

    const handleKeyDown = (e) => {
      if (answers.length === 0) return;
      if (isTypingTarget(e.target)) return;

      const normalizedKey = e.key.toLowerCase();

      switch (normalizedKey) {
        case "arrowup":
          e.preventDefault();
          setSelectedAnswerIndex((prev) => Math.max(0, prev - 1));
          break;
        case "arrowdown":
          e.preventDefault();
          setSelectedAnswerIndex((prev) =>
            Math.min(answers.length - 1, prev + 1),
          );
          break;
        case "a":
          e.preventDefault();
          if (keyboardRef.current && keyboardRef.current.approveAllPending) {
            keyboardRef.current.approveAllPending();
          }
          break;
        case "u":
          e.preventDefault();
          if (keyboardRef.current && keyboardRef.current.unlockImages) {
            keyboardRef.current.unlockImages();
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [answers]);

  // Keep active row in view when selection changes
  useEffect(() => {
    const rowEl = rowRefs.current[selectedAnswerIndex];
    const containerEl = scrollContainerRef.current;

    if (!rowEl || !containerEl) return;

    rowEl.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest",
    });
  }, [selectedAnswerIndex]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* Header with filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          <Typography variant="h6">QA Review - Images</Typography>
          <Grid
            container
            spacing={2}
            sx={{ width: "100%" }}
          >
            <Grid
              xs={12}
              sm={6}
              md={1.5}
            >
              <TextField
                label="Start Date"
                type="date"
                value={dateStart}
                onChange={(e) => setDateStart(e.target.value)}
                slotProps={{
                  inputLabel: { shrink: true },
                }}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid
              xs={12}
              sm={6}
              md={1.5}
            >
              <TextField
                label="End Date"
                type="date"
                value={dateEnd}
                onChange={(e) => setDateEnd(e.target.value)}
                slotProps={{
                  inputLabel: { shrink: true },
                }}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid
              xs={12}
              sm={6}
              md={2.5}
            >
              <TextField
                select
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                fullWidth
                size="small"
                slotProps={{ select: { native: true } }}
                disabled={clientsLoading}
                sx={{ minWidth: "280px" }}
              >
                <option value="">All Clients</option>
                {sortedClients.map((c) => (
                  <option
                    key={c.id}
                    value={c.id}
                  >
                    {c.name}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid
              xs={12}
              sm={6}
              md={2.5}
            >
              <TextField
                select
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                fullWidth
                size="small"
                slotProps={{ select: { native: true } }}
                disabled={projectsLoading}
                sx={{ minWidth: "300px" }}
              >
                <option value="">All Projects</option>
                {sortedProjects.map((p) => (
                  <option
                    key={p.id}
                    value={p.id}
                  >
                    {p.name}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid
              xs={12}
              sm={6}
              md={2.5}
            >
              <TextField
                select
                value={repId}
                onChange={(e) => setRepId(e.target.value)}
                fullWidth
                size="small"
                slotProps={{ select: { native: true } }}
                disabled={repsLoading}
                sx={{ minWidth: "300px" }}
              >
                <option value="">All Reps</option>
                {sortedReps.map((r) => (
                  <option
                    key={r.id}
                    value={r.id}
                  >
                    {r.name} ({r.qaCount})
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid
              xs={12}
              sm={6}
              md={2.5}
            >
              <TextField
                select
                value={province}
                onChange={(e) => setProvince(e.target.value)}
                fullWidth
                size="small"
                slotProps={{ select: { native: true } }}
                sx={{ minWidth: "220px" }}
              >
                <option value="">All Provinces</option>
                {sortedProvinces.map((p) => (
                  <option
                    key={p.id}
                    value={p.id}
                  >
                    {p.name}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid
              xs={12}
              sm={6}
              md={2.0}
            >
              <TextField
                select
                value={reviewRequestedOnly}
                onChange={(e) => setReviewRequestedOnly(e.target.value)}
                fullWidth
                size="small"
                slotProps={{ select: { native: true } }}
                sx={{ minWidth: "220px" }}
              >
                <option value="all">All QA Items</option>
                <option value="review">To Be Reviewed</option>
              </TextField>
            </Grid>
            <Grid
              xs={12}
              sm={6}
              md={2.5}
            >
              <TextField
                select
                value={surveyQuestionId}
                onChange={(e) => setSurveyQuestionId(e.target.value)}
                fullWidth
                size="small"
                slotProps={{ select: { native: true } }}
                disabled={questionsLoading}
                sx={{ minWidth: "260px" }}
              >
                <option value="">All Questions</option>
                {sortedQuestions.map((q) => (
                  <option
                    key={q.id}
                    value={q.id}
                  >
                    {q.name} ({q.qaCount})
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid
              xs={12}
              sm={6}
              md={1.5}
            >
              <TextField
                select
                label="Rows"
                value={pageSize}
                onChange={handlePageSizeChange}
                fullWidth
                size="small"
                slotProps={{ select: { native: true } }}
                sx={{ minWidth: "120px" }}
              >
                {QA_PAGE_SIZE_OPTIONS.map((size) => (
                  <option
                    key={size}
                    value={size}
                  >
                    {size}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid
              xs={12}
              sm={12}
              md={1}
            >
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
                fullWidth
              >
                {loading ? <CircularProgress size={24} /> : "Search"}
              </Button>
            </Grid>
          </Grid>
          {error && (
            <Typography
              color="error"
              variant="body2"
            >
              {error}
            </Typography>
          )}
          {answers.length > 0 && (
            <Stack
              direction="row"
              spacing={1}
              sx={{ flexWrap: "wrap" }}
            >
              <Chip
                label={`Showing ${answers.length} answers`}
                color="primary"
                variant="outlined"
              />
              <Chip
                label="Shortcuts: ↑/↓ active row, A approve pending, U unlock"
                color="secondary"
                variant="outlined"
              />
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Single-stream review rows */}
      <Box
        ref={scrollContainerRef}
        sx={{ flex: 1, minHeight: 0, overflow: "auto", pb: 2 }}
      >
        {loading ? (
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <CircularProgress size={28} />
          </Paper>
        ) : null}

        {!loading && answers.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Typography color="textSecondary">
              No answers requiring QA
            </Typography>
          </Paper>
        ) : null}

        {!loading && answers.length > 0 ? (
          <Stack spacing={1.5}>
            {answers.map((answer, index) => {
              const isSelected = index === selectedAnswerIndex;

              return (
                <Paper
                  ref={(el) => {
                    if (el) {
                      rowRefs.current[index] = el;
                    }
                  }}
                  key={
                    answer.id ||
                    `${answer.callId}-${answer.questionId}-${index}`
                  }
                  variant="outlined"
                  onClick={() => setSelectedAnswerIndex(index)}
                  sx={{
                    p: 1.5,
                    borderColor: isSelected ? "primary.main" : "divider",
                    borderWidth: isSelected ? 4 : 1,
                    cursor: "pointer",
                    backgroundColor: isSelected
                      ? "action.hover"
                      : "background.paper",
                  }}
                >
                  <QADetail
                    answer={answer}
                    onUpdate={() => {
                      // Refetch after update
                      fetchQAData(1, false);
                    }}
                    ref={isSelected ? keyboardRef : null}
                    embedded
                  />
                </Paper>
              );
            })}

            <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
              {hasMore ? (
                <Button
                  variant="outlined"
                  onClick={handleLoadMore}
                  disabled={loadingMore || loading}
                >
                  {loadingMore ? <CircularProgress size={20} /> : "Load More"}
                </Button>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  End of results
                </Typography>
              )}
            </Box>
          </Stack>
        ) : null}
      </Box>
    </Box>
  );
};

export default QA;
